import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, recordVisit } from "../lib/api";
import MadaniPage from "../components/MadaniPage";
import {
  ChevronLeft, ChevronRight, ChevronDown,
  Play, Pause, Repeat, SkipBack, SkipForward,
  Search, Loader2, X, BookOpen, ArrowUpRight, Sparkles,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

/* My Quran — a single reader combining Read (Madani Mushaf), Listen (Qari
   loop player), and Search into one page. /app/mushaf, /app/qari and
   /app/search redirect here (see App.js) so old deep links keep working.
   There used to be a visible Read/Listen/Search tab switcher; it was
   removed as duplicate UI once ReadTab grew its own inline search box and
   Listen toggle — see the note on `tab` below. */

const VALID_TABS = ["read", "listen", "search"];

export default function MyQuran() {
  const { t: tr } = useLanguage();
  const [params] = useSearchParams();
  // The visible Read/Listen/Search tab switcher was removed — ReadTab
  // already has its own inline search box and Listen toggle, which made
  // the outer switcher a duplicate control. `tab` is kept only so an
  // incoming deep link (Sidebar's "Search" nav item, the old /app/qari and
  // /app/search redirects) can still land on the fuller standalone
  // Listen/Search views; there's just no on-page control to switch to them
  // manually anymore.
  const tab = VALID_TABS.includes(params.get("tab")) ? params.get("tab") : "read";

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="my-quran-page">
      <header>
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{tr("myQuran.myQuran")}</span>
          <span className="text-foreground/40">-</span>
          <h1 className="font-display font-semibold text-lg text-forest-700">{tr("myQuran.readListenAndSearch")}</h1>
        </div>
      </header>

      {tab === "read" && <ReadTab />}
      {tab === "listen" && <ListenTab />}
      {tab === "search" && <SearchTab />}
    </div>
  );
}

/* ---------------------------------------------------------------- Read */
const MIN_PAGE = 1;
const MAX_PAGE = 604;

// All 114 surahs with the Madani page each one starts on (quran.com chapter data). The Surah selector jumps there.
const SURAHS = [
  { n: 1, name: "Al-Fatihah", firstPage: 1 },
  { n: 2, name: "Al-Baqarah", firstPage: 2 },
  { n: 3, name: "Ali 'Imran", firstPage: 50 },
  { n: 4, name: "An-Nisa", firstPage: 77 },
  { n: 5, name: "Al-Ma'idah", firstPage: 106 },
  { n: 6, name: "Al-An'am", firstPage: 128 },
  { n: 7, name: "Al-A'raf", firstPage: 151 },
  { n: 8, name: "Al-Anfal", firstPage: 177 },
  { n: 9, name: "At-Tawbah", firstPage: 187 },
  { n: 10, name: "Yunus", firstPage: 208 },
  { n: 11, name: "Hud", firstPage: 221 },
  { n: 12, name: "Yusuf", firstPage: 235 },
  { n: 13, name: "Ar-Ra'd", firstPage: 249 },
  { n: 14, name: "Ibrahim", firstPage: 255 },
  { n: 15, name: "Al-Hijr", firstPage: 262 },
  { n: 16, name: "An-Nahl", firstPage: 267 },
  { n: 17, name: "Al-Isra", firstPage: 282 },
  { n: 18, name: "Al-Kahf", firstPage: 293 },
  { n: 19, name: "Maryam", firstPage: 305 },
  { n: 20, name: "Taha", firstPage: 312 },
  { n: 21, name: "Al-Anbya", firstPage: 322 },
  { n: 22, name: "Al-Hajj", firstPage: 332 },
  { n: 23, name: "Al-Mu'minun", firstPage: 342 },
  { n: 24, name: "An-Nur", firstPage: 350 },
  { n: 25, name: "Al-Furqan", firstPage: 359 },
  { n: 26, name: "Ash-Shu'ara", firstPage: 367 },
  { n: 27, name: "An-Naml", firstPage: 377 },
  { n: 28, name: "Al-Qasas", firstPage: 385 },
  { n: 29, name: "Al-'Ankabut", firstPage: 396 },
  { n: 30, name: "Ar-Rum", firstPage: 404 },
  { n: 31, name: "Luqman", firstPage: 411 },
  { n: 32, name: "As-Sajdah", firstPage: 415 },
  { n: 33, name: "Al-Ahzab", firstPage: 418 },
  { n: 34, name: "Saba", firstPage: 428 },
  { n: 35, name: "Fatir", firstPage: 434 },
  { n: 36, name: "Ya-Sin", firstPage: 440 },
  { n: 37, name: "As-Saffat", firstPage: 446 },
  { n: 38, name: "Sad", firstPage: 453 },
  { n: 39, name: "Az-Zumar", firstPage: 458 },
  { n: 40, name: "Ghafir", firstPage: 467 },
  { n: 41, name: "Fussilat", firstPage: 477 },
  { n: 42, name: "Ash-Shuraa", firstPage: 483 },
  { n: 43, name: "Az-Zukhruf", firstPage: 489 },
  { n: 44, name: "Ad-Dukhan", firstPage: 496 },
  { n: 45, name: "Al-Jathiyah", firstPage: 499 },
  { n: 46, name: "Al-Ahqaf", firstPage: 502 },
  { n: 47, name: "Muhammad", firstPage: 507 },
  { n: 48, name: "Al-Fath", firstPage: 511 },
  { n: 49, name: "Al-Hujurat", firstPage: 515 },
  { n: 50, name: "Qaf", firstPage: 518 },
  { n: 51, name: "Adh-Dhariyat", firstPage: 520 },
  { n: 52, name: "At-Tur", firstPage: 523 },
  { n: 53, name: "An-Najm", firstPage: 526 },
  { n: 54, name: "Al-Qamar", firstPage: 528 },
  { n: 55, name: "Ar-Rahman", firstPage: 531 },
  { n: 56, name: "Al-Waqi'ah", firstPage: 534 },
  { n: 57, name: "Al-Hadid", firstPage: 537 },
  { n: 58, name: "Al-Mujadila", firstPage: 542 },
  { n: 59, name: "Al-Hashr", firstPage: 545 },
  { n: 60, name: "Al-Mumtahanah", firstPage: 549 },
  { n: 61, name: "As-Saf", firstPage: 551 },
  { n: 62, name: "Al-Jumu'ah", firstPage: 553 },
  { n: 63, name: "Al-Munafiqun", firstPage: 554 },
  { n: 64, name: "At-Taghabun", firstPage: 556 },
  { n: 65, name: "At-Talaq", firstPage: 558 },
  { n: 66, name: "At-Tahrim", firstPage: 560 },
  { n: 67, name: "Al-Mulk", firstPage: 562 },
  { n: 68, name: "Al-Qalam", firstPage: 564 },
  { n: 69, name: "Al-Haqqah", firstPage: 566 },
  { n: 70, name: "Al-Ma'arij", firstPage: 568 },
  { n: 71, name: "Nuh", firstPage: 570 },
  { n: 72, name: "Al-Jinn", firstPage: 572 },
  { n: 73, name: "Al-Muzzammil", firstPage: 574 },
  { n: 74, name: "Al-Muddaththir", firstPage: 575 },
  { n: 75, name: "Al-Qiyamah", firstPage: 577 },
  { n: 76, name: "Al-Insan", firstPage: 578 },
  { n: 77, name: "Al-Mursalat", firstPage: 580 },
  { n: 78, name: "An-Naba", firstPage: 582 },
  { n: 79, name: "An-Nazi'at", firstPage: 583 },
  { n: 80, name: "'Abasa", firstPage: 585 },
  { n: 81, name: "At-Takwir", firstPage: 586 },
  { n: 82, name: "Al-Infitar", firstPage: 587 },
  { n: 83, name: "Al-Mutaffifin", firstPage: 587 },
  { n: 84, name: "Al-Inshiqaq", firstPage: 589 },
  { n: 85, name: "Al-Buruj", firstPage: 590 },
  { n: 86, name: "At-Tariq", firstPage: 591 },
  { n: 87, name: "Al-A'la", firstPage: 591 },
  { n: 88, name: "Al-Ghashiyah", firstPage: 592 },
  { n: 89, name: "Al-Fajr", firstPage: 593 },
  { n: 90, name: "Al-Balad", firstPage: 594 },
  { n: 91, name: "Ash-Shams", firstPage: 595 },
  { n: 92, name: "Al-Layl", firstPage: 595 },
  { n: 93, name: "Ad-Duhaa", firstPage: 596 },
  { n: 94, name: "Ash-Sharh", firstPage: 596 },
  { n: 95, name: "At-Tin", firstPage: 597 },
  { n: 96, name: "Al-'Alaq", firstPage: 597 },
  { n: 97, name: "Al-Qadr", firstPage: 598 },
  { n: 98, name: "Al-Bayyinah", firstPage: 598 },
  { n: 99, name: "Az-Zalzalah", firstPage: 599 },
  { n: 100, name: "Al-'Adiyat", firstPage: 599 },
  { n: 101, name: "Al-Qari'ah", firstPage: 600 },
  { n: 102, name: "At-Takathur", firstPage: 600 },
  { n: 103, name: "Al-'Asr", firstPage: 601 },
  { n: 104, name: "Al-Humazah", firstPage: 601 },
  { n: 105, name: "Al-Fil", firstPage: 601 },
  { n: 106, name: "Quraysh", firstPage: 602 },
  { n: 107, name: "Al-Ma'un", firstPage: 602 },
  { n: 108, name: "Al-Kawthar", firstPage: 602 },
  { n: 109, name: "Al-Kafirun", firstPage: 603 },
  { n: 110, name: "An-Nasr", firstPage: 603 },
  { n: 111, name: "Al-Masad", firstPage: 603 },
  { n: 112, name: "Al-Ikhlas", firstPage: 604 },
  { n: 113, name: "Al-Falaq", firstPage: 604 },
  { n: 114, name: "An-Nas", firstPage: 604 },
];

/* Standard Madani-mushaf juz start pages (30 juz of 20 pages, juz 1 has 21). Used by the Juz jump. */
const JUZ_START_PAGE = {
  1: 1, 2: 22, 3: 42, 4: 62, 5: 82, 6: 102, 7: 122, 8: 142, 9: 162, 10: 182,
  11: 202, 12: 222, 13: 242, 14: 262, 15: 282, 16: 302, 17: 322, 18: 342, 19: 362, 20: 382,
  21: 402, 22: 422, 23: 442, 24: 462, 25: 482, 26: 502, 27: 522, 28: 542, 29: 562, 30: 582,
};

/* The surah a page belongs to when no page data has arrived yet: the last surah that starts on or before it. */
const surahOnPage = (page) => {
  let found = SURAHS[0].n;
  for (const x of SURAHS) if (x.firstPage <= page) found = x.n;
  return found;
};

/* The unified reader (UM1 + UC3).

   Layout, top to bottom: a full-width Search field, then a collapsible
   toolbar card (Listen toggle + Surah/Verse/Page/Juz jump pills) that
   starts hidden — the mushaf page's own header ("{Surah} · Page N · Juz X
   · Hizb Y") doubles as the button that shows/hides it, so the resting
   reading view is just the page. Then the mushaf itself, with floating
   left/right page-turn arrows over its edges (swipe also turns pages on
   touch).

   Crucially, Search and Listen open *inline* panels rather than switching
   tab or route (UC3) — choosing to listen used to unmount the mushaf
   entirely, which is what the client flagged as a bug. The page stays
   mounted underneath both panels the whole time. */
function ReadTab() {
  const { t: tr } = useLanguage();
  const [page, setPage] = useState(1);
  const [searchQ, setSearchQ] = useState(""); // drives the always-visible search box
  const [listenOpen, setListenOpen] = useState(false);
  const [controlsOpen, setControlsOpen] = useState(false); // toolbar card starts hidden; the mushaf header toggles it
  const [pageInfo, setPageInfo] = useState(null); // N1 — {surah, ayah, juz, hizb} of the current page, reported by MadaniPage
  // The surah of the first verse on this page once its data has loaded; before that, a guess from the page number.
  const inferSurah = pageInfo?.surah ?? surahOnPage(page);

  const nextPage = () => setPage((n) => Math.min(MAX_PAGE, n + 1));
  const prevPage = () => setPage((n) => Math.max(MIN_PAGE, n - 1));

  /* The reader toolbar — Listen toggle + Surah/Verse/Page/Juz jump pills.
     Passed into MadaniPage as `belowHeader` (only while controlsOpen) so it
     expands in the gap directly under the page's "{Surah} · Page N · Juz X
     · Hizb Y" header — the button that toggles it — rather than up by the
     search field. */
  const toolbar = (
    // Not `hifz-card` here — its p-6 is too much for a panel nested inside
    // the mushaf page's own padding; p-4 keeps the four jump pills on one
    // line inside the narrow frame.
    <div className="bg-white rounded-2xl border border-sand-300 p-4 space-y-3" data-testid="reader-toolbar-card">
      {/* Row 1 — Listen toggle only (Search lives above the reader). */}
      <div className="flex items-center justify-end" data-testid="reader-actions">
        <button
          type="button"
          onClick={() => setListenOpen((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm border transition shrink-0 ${
            listenOpen ? "bg-forest-700 text-sand-100 border-forest-700" : "border-sand-300 hover:bg-sand-100"
          }`}
          data-testid="reader-listen-toggle"
        >
          <Play className="h-4 w-4" /> {tr("myQuran.listen")}
        </button>
      </div>

      {/* Row 2 — Surah · Verse · Page · Juz jumps. Each pill IS the button:
          appearance-none drops the native arrow, our ChevronDown is the
          "opens a dropdown" cue, the field name is baked into the option
          text so the closed control is self-explanatory. flex-nowrap keeps
          all four on one line; each <select> stays exactly as wide as its
          own content (shrink-0, no flex-grow) so nothing truncates and
          nothing is wider than it needs to be; justify-center keeps them a
          tight centered group. */}
      <div className="flex items-center justify-center gap-2 flex-nowrap" data-testid="reader-jumps">
        <div className="relative shrink-0">
          <select
            value={inferSurah}
            onChange={(e) => setPage(SURAHS.find((x) => x.n === parseInt(e.target.value)).firstPage)}
            className="appearance-none rounded-full border border-sand-300 bg-white pl-2.5 pr-6 py-1.5 text-xs font-medium text-forest-700 hover:bg-sand-100 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-forest-600/40"
            aria-label={tr("myQuran.jumpToSurah")}
            data-testid="mushaf-surah-select"
          >
            {SURAHS.map((x) => <option key={x.n} value={x.n}>{x.n} · {x.name}</option>)}
          </select>
          <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
        </div>

        <VerseJump surah={inferSurah} onJump={setPage} />

        <div className="relative shrink-0">
          <select
            value={page}
            onChange={(e) => setPage(parseInt(e.target.value))}
            className="appearance-none rounded-full border border-sand-300 bg-white pl-2.5 pr-6 py-1.5 text-xs font-medium text-forest-700 hover:bg-sand-100 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-forest-600/40"
            aria-label={tr("myQuran.jumpToPage")}
            data-testid="mushaf-page-select"
          >
            {/* C2 — plain page numbers only; the surah selector beside this
                one already carries the surah name. */}
            {Array.from({ length: MAX_PAGE - MIN_PAGE + 1 }, (_, i) => MIN_PAGE + i).map((n) => (
              <option key={n} value={n}>{tr("myQuran.page", { n })}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
        </div>

        <div className="relative shrink-0">
          <select
            value={Object.keys(JUZ_START_PAGE).reverse().find((j) => page >= JUZ_START_PAGE[j]) || 1}
            onChange={(e) => setPage(JUZ_START_PAGE[e.target.value])}
            className="appearance-none rounded-full border border-sand-300 bg-white pl-2.5 pr-6 py-1.5 text-xs font-medium text-forest-700 hover:bg-sand-100 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-forest-600/40"
            aria-label={tr("myQuran.jumpToJuz")}
            data-testid="mushaf-juz-select"
          >
            {Object.keys(JUZ_START_PAGE).map((j) => <option key={j} value={j}>{tr("myQuran.juz", { j })}</option>)}
          </select>
          <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="quran-read-tab">
      {/* Search lives above the toolbar card now (was a boxed-in field on
          the first row inside it) — typing still opens the results panel
          below automatically. */}
      <form onSubmit={(e) => e.preventDefault()} className="relative" data-testid="reader-search-box">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder={tr("myQuran.searchTheQuran")}
          className="w-full rounded-xl border border-sand-300 bg-white pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-600/40"
          data-testid="reader-search-input"
        />
        {searchQ && (
          <button
            type="button"
            onClick={() => setSearchQ("")}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 px-2 py-1 rounded-lg text-xs text-muted-foreground hover:bg-sand-200"
            aria-label={tr("myQuran.clearSearch")}
            data-testid="reader-search-clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </form>

      {/* The reader toolbar (Listen + Surah/Verse/Page/Juz jumps) is the
          `toolbar` const above — handed to MadaniPage as `belowHeader` so
          it opens in the gap directly under the page's own header, which
          is the button that toggles it. */}

      {/* Inline panels — the mushaf below stays mounted (UC3) */}
      {searchQ.trim().length >= 2 && (
        <InlineSearchPanel query={searchQ} onJump={(n) => setPage(n)} onClose={() => setSearchQ("")} />
      )}
      {listenOpen && <InlineListenPanel page={page} onClose={() => setListenOpen(false)} />}

      {/* Page turning: the Next/Prev button row is gone — floating arrows
          over the page's own left/right edges instead, plus swipe on touch
          (handled inside MadaniPage via onSwipeNext/onSwipePrev). The
          Mushaf reads right-to-left, so paging "forward" moves the eye
          leftward: the LEFT arrow advances the page number, the RIGHT arrow
          goes back — same as turning a real Arabic book. Each arrow fades
          out at its boundary (page 1 / last cached page).

          The arrows sit in a zero-height `sticky top-[50vh]` layer, not on
          `absolute top-1/2` of this wrapper: a full 15-line page is two-plus
          screens tall, so top-1/2 put the arrows far down the page (off
          screen unless you scrolled to the exact middle) — which is why
          they looked "missing" on the denser pages. Sticky keeps them
          pinned to the vertical middle of the viewport the whole time the
          page is in view. */}
      <div className="relative">
        <div className="pointer-events-none sticky top-[50vh] z-30 h-0">
          <button
            type="button"
            onClick={nextPage}
            disabled={page >= MAX_PAGE}
            aria-label={tr("myQuran.nextPage")}
            className="pointer-events-auto absolute left-1 -translate-y-1/2 h-7 w-7 rounded-full bg-forest-700/85 hover:bg-forest-700 text-sand-100 shadow grid place-items-center backdrop-blur-sm transition disabled:opacity-0 disabled:pointer-events-none"
            data-testid="mushaf-next"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={prevPage}
            disabled={page <= MIN_PAGE}
            aria-label={tr("myQuran.previousPage")}
            className="pointer-events-auto absolute right-1 -translate-y-1/2 h-7 w-7 rounded-full bg-forest-700/85 hover:bg-forest-700 text-sand-100 shadow grid place-items-center backdrop-blur-sm transition disabled:opacity-0 disabled:pointer-events-none"
            data-testid="mushaf-prev"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <MadaniPage
          pageNo={page}
          readerMode
          onSwipeNext={nextPage}
          onSwipePrev={prevPage}
          onPageInfo={setPageInfo}
          onHeaderClick={() => setControlsOpen((v) => !v)}
          headerExpanded={controlsOpen}
          belowHeader={controlsOpen ? toolbar : null}
        />
      </div>

      <p className="text-center text-foreground/70">{tr("myQuran.everythingForEngagingWithThe")}</p>

      <p className="text-[11px] text-center text-muted-foreground">
        {tr("myQuran.authenticKfgqpcV1PageFont")}
      </p>

      {/* N1 — floating shortcut into Tadabbur for whatever's on screen right
          now, so a reader doesn't have to leave, remember their place, then
          go hunt through the sidebar for the matching section. Both surahs
          this reader supports (Fatihah, Baqarah) have a full Tafsir
          Explorer structure, so this always has somewhere real to land —
          no placeholder/dead-end case to handle. Placement/scope wasn't
          pinned down in the report itself ("confirm with the team before
          building"); this is a deliberately modest interpretation — a
          deep-link shortcut, not an inline tadabbur reader grafted into the
          mushaf view, which would be a much bigger, riskier undertaking
          without an actual design to build against. */}
      <Link
        to={`/app/tadabbur/${pageInfo?.surah || inferSurah}${pageInfo?.ayah ? `?ayah=${pageInfo.ayah}` : ""}`}
        className="absolute bottom-[calc(var(--mobile-tabbar-h)_+_env(safe-area-inset-bottom)_+_1rem)] right-6 z-40 h-12 w-12 rounded-full bg-forest-700 text-sand-100 shadow-lg grid place-items-center hover:bg-forest-600 transition"
        title={tr("myQuran.openTadabburForThisPage")}
        aria-label={tr("myQuran.openTadabburForThisPage")}
        data-testid="reader-tadabbur-fab"
      >
        <Sparkles className="h-5 w-5" />
      </Link>
    </div>
  );
}

/* Verse jump — resolves an ayah to the page it sits on, server-side, so the
   reader can jump straight to it (the mushaf is paginated, not per-verse). */
function VerseJump({ surah, onJump }) {
  const { t: tr } = useLanguage();
  const [ayahs, setAyahs] = useState([]);
  const [value, setValue] = useState("");

  useEffect(() => {
    setValue("");
    api.get(`/quran/surahs/${surah}`)
      .then((r) => setAyahs((r.data?.ayahs || []).map((a) => a.n)))
      .catch(() => setAyahs([]));
  }, [surah]);

  const jump = (n) => {
    setValue(n);
    if (!n) return;
    api.get(`/quran/ayah-page/${surah}/${n}`)
      .then((r) => { if (r.data?.page) onJump(r.data.page); })
      .catch(() => {});
  };

  return (
    <div className="relative shrink-0">
      <select
        value={value}
        onChange={(e) => jump(e.target.value)}
        className="appearance-none rounded-full border border-sand-300 bg-white pl-2.5 pr-6 py-1.5 text-xs font-medium text-forest-700 hover:bg-sand-100 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-forest-600/40"
        aria-label={tr("myQuran.jumpToVerse")}
        data-testid="mushaf-verse-select"
      >
        {/* Placeholder: `disabled hidden` so it's the closed-pill label
            while nothing's picked, but doesn't show as its own row in the
            open list next to the real "Verse 1", "Verse 2"… options. */}
        <option value="" disabled hidden>{tr("myQuran.verse")}</option>
        {ayahs.map((n) => <option key={n} value={n}>{tr("myQuran.verse2", { n })}</option>)}
      </select>
      <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
    </div>
  );
}

/* Inline search results (UC3 + UM1) — the query box itself now lives in
   Row 1 (always visible, matching the client's reference layout); this
   panel is just the results list underneath, driven by that box's value.
   Results still jump the mushaf in place rather than navigating away. */
function InlineSearchPanel({ query, onJump, onClose }) {
  const { tx, t: tr } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) { setData(null); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      api.get(`/quran/search?${new URLSearchParams({ q: trimmed }).toString()}`)
        .then((r) => setData(r.data))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    }, 280);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const openResult = (r) => {
    api.get(`/quran/ayah-page/${r.surah_number}/${r.verse_number}`)
      .then((res) => { if (res.data?.page) onJump(res.data.page); })
      .catch(() => {});
  };

  // C3 — a query can also match a surah by name/translation (e.g. "mulk",
  // "yaseen") rather than any literal ayah text. This reader only renders
  // the Madani page glyphs for the two surahs in SURAHS (Al-Fatihah,
  // Al-Baqarah), so a matched surah outside that set can't actually be
  // opened here yet — say so plainly instead of pretending to jump.
  const openSurah = (s) => {
    const inScope = SURAHS.find((x) => x.n === s.number);
    if (inScope) { onJump(inScope.firstPage); return; }
    toast.info(tr("myQuran.surahIsnTInThe", { name_en: s.name_en }));
  };

  const hasResults = (data?.results?.length || 0) > 0 || (data?.surah_matches?.length || 0) > 0;

  return (
    <div className="hifz-card space-y-3" data-testid="reader-search-panel">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">{tr("myQuran.resultsFor", { query })}</span>
        <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-sand-200" aria-label={tr("myQuran.closeSearch")} data-testid="reader-search-close">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {loading && <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {tr("myQuran.searching")}</div>}
      {data && data.surah_matches?.length > 0 && (
        <div className="space-y-1" data-testid="reader-search-surah-matches">
          {data.surah_matches.map((s) => (
            <button
              key={s.number}
              type="button"
              onClick={() => openSurah(s)}
              className="w-full text-left py-2 px-2.5 rounded-xl bg-sand-100 hover:bg-sand-200 transition flex items-center justify-between gap-2"
              data-testid={`reader-search-surah-${s.number}`}
            >
              <span>
                <span className="text-sm font-semibold text-forest-700">{tr("myQuran.surah", { number: s.number, name_en: s.name_en })}</span>
                <span className="text-xs text-muted-foreground ml-1.5">{s.name_translation}</span>
              </span>
              <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}
      {data && data.results?.length > 0 && (
        <div className="max-h-72 overflow-y-auto divide-y divide-sand-200" data-testid="reader-search-results">
          {data.results.slice(0, 20).map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => openResult(r)}
              className="w-full text-left py-2.5 px-1 hover:bg-sand-100 transition rounded"
              data-testid={`reader-search-result-${r.surah_number}-${r.verse_number}`}
            >
              <div className="text-[11px] text-forest-700 font-semibold">{tr("myQuran.surahAyah", { surah_number: r.surah_number, verse_number: r.verse_number })}</div>
              <div className="ayah-arabic text-lg text-foreground/90 leading-[2]" dir="rtl">{tx(r.text)}</div>
            </button>
          ))}
        </div>
      )}
      {data && !hasResults && !loading && (
        <p className="text-xs text-muted-foreground">{tr("myQuran.noMatchesFor", { query: data.query })}</p>
      )}
      {!data && !loading && (
        <p className="text-xs text-muted-foreground">{tr("myQuran.resultsJumpThisPageThe")}</p>
      )}
    </div>
  );
}

/* Inline listen (UC3) — plays right here in the reading view rather than
   navigating away. Carries the same Surah/From/To range picker as the
   standalone Listen tab, and (C6) the fuller loop/speed/now-playing
   controls too — "More options" used to link to /app/quran?tab=listen,
   but that swaps the whole reading view out for a different tab, which is
   exactly the "jumps to a separate page" bug being fixed here. It now just
   expands this same panel in place; the mushaf underneath never unmounts.
   The standalone ListenTab below is left as-is for old /app/qari deep
   links — this panel just no longer sends anyone there itself. */
function InlineListenPanel({ page, onClose }) {
  const { t: tr } = useLanguage();
  const inferSurah = page === 1 ? 1 : 2;
  const [qaris, setQaris] = useState([]);
  const [qari, setQari] = useState(null);
  const [surahs, setSurahs] = useState([]);
  const [surah, setSurah] = useState(inferSurah);
  const [data, setData] = useState(null);
  const [ayahStart, setAyahStart] = useState(1);
  const [ayahEnd, setAyahEnd] = useState(1);
  const [currentAyah, setCurrentAyah] = useState(1);
  const [playing, setPlaying] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [loops, setLoops] = useState(1);
  const [loopLeft, setLoopLeft] = useState(0);
  const [rate, setRate] = useState(1);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/qaris").then((r) => { setQaris(r.data || []); setQari((r.data || [])[0] || null); }).catch(() => {});
    api.get("/quran/surahs").then((r) => setSurahs(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/quran/surahs/${surah}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [surah]);

  useEffect(() => {
    if (!data) return;
    const max = data.ayahs.length;
    if (ayahStart > max) setAyahStart(1);
    if (ayahEnd > max) setAyahEnd(Math.min(max, ayahStart));
    if (ayahEnd < ayahStart) setAyahEnd(ayahStart);
    if (currentAyah < ayahStart || currentAyah > ayahEnd) setCurrentAyah(ayahStart);
  }, [data, ayahStart, ayahEnd, currentAyah]);

  const rangeSize = Math.max(1, ayahEnd - ayahStart + 1);
  const audioUrl = qari
    ? qari.url_pattern
        .replace("{surah:03d}", String(surah).padStart(3, "0"))
        .replace("{ayah:03d}", String(currentAyah).padStart(3, "0"))
    : null;

  const toggle = () => {
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    setCurrentAyah(ayahStart);
    setLoopLeft(loops);
    setPlaying(true);
    setTimeout(() => audioRef.current?.play().catch(() => setPlaying(false)), 60);
  };

  const onEnded = () => {
    if (currentAyah < ayahEnd) {
      setCurrentAyah(currentAyah + 1);
      setTimeout(() => audioRef.current?.play().catch(() => setPlaying(false)), 60);
    } else if (loopLeft > 1) {
      setLoopLeft(loopLeft - 1);
      setCurrentAyah(ayahStart);
      setTimeout(() => audioRef.current?.play().catch(() => setPlaying(false)), 60);
    } else {
      setPlaying(false);
      setCurrentAyah(ayahStart);
    }
  };

  const onLoadedMeta = () => {
    if (audioRef.current && rate !== 1) audioRef.current.playbackRate = rate;
  };

  return (
    <div className="hifz-card space-y-3" data-testid="reader-listen-panel">
      {/* Row 1: compact Play + Qari + close. Row 2: Surah + From/To + count,
          kept on one non-wrapping line (selects shrink instead of wrapping). */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={toggle}
          disabled={!audioUrl}
          className="btn-primary text-xs !px-3 !py-1.5 disabled:opacity-40 shrink-0"
          data-testid="reader-listen-play"
        >
          {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          {playing ? tr("myQuran.pause") : tr("myQuran.play")}
        </button>
        <select
          value={qari?.id || ""}
          onChange={(e) => setQari(qaris.find((x) => x.id === e.target.value))}
          className="min-w-0 flex-1 rounded-xl border border-sand-300 bg-white px-2.5 py-1.5 text-sm"
          data-testid="reader-listen-qari"
        >
          {qaris.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
        <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-sand-200 shrink-0" aria-label={tr("myQuran.closeListen")} data-testid="reader-listen-close">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex items-center gap-1.5 flex-nowrap">
        <select
          value={surah}
          onChange={(e) => { setSurah(parseInt(e.target.value)); setAyahStart(1); setAyahEnd(1); }}
          className="min-w-0 flex-1 rounded-xl border border-sand-300 bg-white px-2.5 py-1.5 text-sm"
          data-testid="reader-listen-surah"
        >
          {surahs.map((s) => <option key={s.number} value={s.number}>{`${s.number} · ${s.name_en}`}</option>)}
        </select>
        <label className="text-xs text-muted-foreground shrink-0">{tr("myQuran.from")}</label>
        <select
          value={ayahStart}
          onChange={(e) => { const n = parseInt(e.target.value); setAyahStart(n); if (n > ayahEnd) setAyahEnd(n); }}
          className="rounded-xl border border-sand-300 bg-white px-2 py-1.5 text-sm shrink-0"
          data-testid="reader-listen-ayah-start"
        >
          {(data?.ayahs || []).map((a) => <option key={a.n} value={a.n}>{a.n}</option>)}
        </select>
        <label className="text-xs text-muted-foreground shrink-0">{tr("myQuran.to")}</label>
        <select
          value={ayahEnd}
          onChange={(e) => setAyahEnd(parseInt(e.target.value))}
          className="rounded-xl border border-sand-300 bg-white px-2 py-1.5 text-sm shrink-0"
          data-testid="reader-listen-ayah-end"
        >
          {(data?.ayahs || []).filter((a) => a.n >= ayahStart).map((a) => <option key={a.n} value={a.n}>{a.n}</option>)}
        </select>
        <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap" data-testid="reader-listen-range-count">
          {tr("common.ayahCount", { count: rangeSize })}
        </span>
      </div>

      {/* "More options" sits on its own line, bottom-right of the block —
          C6: this now expands the same panel in place instead of linking
          away, so the mushaf page underneath never unmounts. */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground" data-testid="reader-listen-current">
          {playing ? tr("myQuran.ayah", { surah, currentAyah }) : ""}
        </span>
        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          className="text-xs text-forest-700 hover:underline"
          data-testid="reader-listen-full"
        >
          {showMore ? tr("myQuran.fewerOptions") : tr("myQuran.moreOptions")}
        </button>
      </div>

      {/* C? — the "Now playing" preview (ayah text + translation + its own
          big play/skip controls) was removed entirely per instruction —
          playback is still fully controlled by the compact Play/Pause
          button in the row above; skipAyah (which only those two skip
          buttons called) went with it as dead code. What's left here is
          just the Loop range / Speed settings, forced onto one line
          (flex-nowrap, tighter gap) instead of wrapping onto two. */}
      {showMore && (
        <div className="pt-3 border-t border-sand-200" data-testid="reader-listen-more">
          <div className="flex flex-nowrap items-center justify-center gap-3 text-sm">
            <label className="flex items-center gap-1.5 shrink-0">
              <Repeat className="h-4 w-4 text-forest-700" /> {tr("myQuran.loop")}
              <input
                type="number" min={1} max={20} value={loops}
                onChange={(e) => setLoops(parseInt(e.target.value) || 1)}
                className="w-14 rounded-lg border border-sand-300 px-2 py-1 text-sm"
                data-testid="reader-listen-loops"
              />
              <span className="text-xs text-muted-foreground">×</span>
            </label>
            <label className="flex items-center gap-1.5 shrink-0">{tr("myQuran.speed")}
              <select
                value={rate}
                onChange={(e) => {
                  const r = parseFloat(e.target.value);
                  setRate(r);
                  if (audioRef.current) audioRef.current.playbackRate = r;
                }}
                className="rounded-lg border border-sand-300 px-2 py-1 text-sm"
                data-testid="reader-listen-rate"
              >
                {[0.5, 0.75, 1, 1.25, 1.5].map((r) => <option key={r} value={r}>{r}x</option>)}
              </select>
            </label>
          </div>
          {playing && (
            <div className="mt-2 text-center text-xs text-muted-foreground" data-testid="reader-listen-loop-progress">
              {tr("myQuran.loopProgress", { loop: loops - loopLeft + 1, loops, ayah: currentAyah - ayahStart + 1, rangeSize })}
            </div>
          )}
        </div>
      )}

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={onEnded}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          onLoadedMetadata={onLoadedMeta}
          data-testid="reader-listen-audio"
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------- Listen */
function ListenTab() {
  const { t: tr } = useLanguage();
  const [qaris, setQaris] = useState([]);
  const [surahs, setSurahs] = useState([]);
  const [qari, setQari] = useState(null);
  const [surah, setSurah] = useState(1);
  const [data, setData] = useState(null);
  const [ayahStart, setAyahStart] = useState(1);
  const [ayahEnd, setAyahEnd] = useState(1);
  const [currentAyah, setCurrentAyah] = useState(1);
  const [playing, setPlaying] = useState(false);
  const [loops, setLoops] = useState(1);
  const [loopLeft, setLoopLeft] = useState(0);
  const [rate, setRate] = useState(1);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/qaris").then((r) => { setQaris(r.data); setQari(r.data[0]); });
    api.get("/quran/surahs").then((r) => setSurahs(r.data));
  }, []);
  useEffect(() => {
    api.get(`/quran/surahs/${surah}`).then((r) => setData(r.data));
  }, [surah]);

  useEffect(() => {
    const t = setTimeout(() => recordVisit({
      kind: "qari", surah, ayah: ayahStart, ayah_end: ayahEnd,
      path: `/app/quran?tab=listen`,
    }), 1500);
    return () => clearTimeout(t);
  }, [surah, ayahStart, ayahEnd]);

  useEffect(() => {
    if (!data) return;
    const max = data.ayahs.length;
    if (ayahStart > max) setAyahStart(1);
    if (ayahEnd > max) setAyahEnd(Math.min(max, ayahStart));
    if (ayahEnd < ayahStart) setAyahEnd(ayahStart);
    if (currentAyah < ayahStart || currentAyah > ayahEnd) setCurrentAyah(ayahStart);
  }, [data, ayahStart, ayahEnd, currentAyah]);

  const rangeAyahs = useMemo(
    () => (data?.ayahs || []).filter((a) => a.n >= ayahStart && a.n <= ayahEnd),
    [data, ayahStart, ayahEnd],
  );
  const rangeSize = rangeAyahs.length || 1;

  const audioUrl = qari
    ? qari.url_pattern
        .replace("{surah:03d}", String(surah).padStart(3, "0"))
        .replace("{ayah:03d}", String(currentAyah).padStart(3, "0"))
    : null;

  const play = () => {
    setCurrentAyah(ayahStart);
    setLoopLeft(loops);
    setTimeout(() => audioRef.current?.play().catch(() => {}), 60);
  };

  const onEnded = () => {
    if (currentAyah < ayahEnd) {
      const next = currentAyah + 1;
      setCurrentAyah(next);
      setTimeout(() => audioRef.current?.play().catch(() => {}), 60);
    } else if (loopLeft > 1) {
      setLoopLeft(loopLeft - 1);
      setCurrentAyah(ayahStart);
      setTimeout(() => audioRef.current?.play().catch(() => {}), 60);
    } else {
      setPlaying(false);
    }
  };

  const onLoadedMeta = () => {
    if (audioRef.current && rate !== 1) audioRef.current.playbackRate = rate;
  };

  const ayahDoc = data?.ayahs?.find((a) => a.n === currentAyah);

  return (
    <div className="hifz-card" data-testid="quran-listen-tab">
      <div className="flex flex-wrap gap-3 items-center">
        <select data-testid="select-qari" value={qari?.id || ""} onChange={(e) => setQari(qaris.find((q) => q.id === e.target.value))}
          className="rounded-xl border border-sand-300 bg-white px-4 py-2 text-sm">
          {qaris.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
        </select>
        <select data-testid="qari-select-surah" value={surah} onChange={(e) => { setSurah(parseInt(e.target.value)); setAyahStart(1); setAyahEnd(1); }}
          className="rounded-xl border border-sand-300 bg-white px-4 py-2 text-sm">
          {surahs.map((s) => <option key={s.number} value={s.number}>{`${s.number} · ${s.name_en}`}</option>)}
        </select>
        <label className="text-sm text-muted-foreground">{tr("myQuran.from")}</label>
        <select data-testid="qari-select-ayah-start" value={ayahStart}
          onChange={(e) => { const n = parseInt(e.target.value); setAyahStart(n); if (n > ayahEnd) setAyahEnd(n); }}
          className="rounded-xl border border-sand-300 bg-white px-4 py-2 text-sm">
          {(data?.ayahs || []).map((a) => <option key={a.n} value={a.n}>{a.n}</option>)}
        </select>
        <label className="text-sm text-muted-foreground">{tr("myQuran.to")}</label>
        <select data-testid="qari-select-ayah-end" value={ayahEnd} onChange={(e) => setAyahEnd(parseInt(e.target.value))}
          className="rounded-xl border border-sand-300 bg-white px-4 py-2 text-sm">
          {(data?.ayahs || []).filter((a) => a.n >= ayahStart).map((a) => <option key={a.n} value={a.n}>{a.n}</option>)}
        </select>
        <span className="text-xs text-muted-foreground" data-testid="qari-range-count">
          {tr("common.ayahCount", { count: rangeSize })}
        </span>
      </div>

      {ayahDoc && (
        <div className="mt-8 text-center" data-testid="qari-current">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
            {tr("myQuran.nowPlayingAyah", { n: ayahDoc.n })}
            {ayahEnd !== ayahStart && <> {tr("myQuran.range", { ayahStart, ayahEnd })}</>}
          </div>
          <div className="ayah-arabic text-4xl md:text-5xl mt-2 text-forest-700" data-testid="qari-arabic">
            {ayahDoc.ar}
          </div>
          {ayahDoc.en && <div className="mt-2 text-sm text-foreground/70">{ayahDoc.en}</div>}
        </div>
      )}

      <div className="mt-8 flex flex-col items-center gap-5">
        {audioUrl && (
          <audio
            ref={audioRef}
            src={audioUrl}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={onEnded}
            onLoadedMetadata={onLoadedMeta}
            data-testid="audio-el"
          />
        )}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setCurrentAyah(Math.max(ayahStart, currentAyah - 1))}
            className="btn-ghost p-3"
            data-testid="prev-ayah"
            aria-label={tr("myQuran.previousAyah")}
          >
            <SkipBack className="h-5 w-5" />
          </button>
          <button
            onClick={() => (playing ? audioRef.current.pause() : play())}
            className="btn-primary h-14 w-14 p-0 grid place-items-center"
            data-testid="qari-play"
            aria-label={playing ? tr("myQuran.pause") : tr("myQuran.play")}
          >
            {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
          </button>
          <button
            onClick={() => setCurrentAyah(Math.min(ayahEnd, currentAyah + 1))}
            className="btn-ghost p-3"
            data-testid="next-ayah"
            aria-label={tr("myQuran.nextAyah")}
          >
            <SkipForward className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <label className="flex items-center gap-2">
            <Repeat className="h-4 w-4 text-forest-700" /> {tr("myQuran.loopRange")}
            <input
              type="number" min={1} max={20} value={loops}
              onChange={(e) => setLoops(parseInt(e.target.value) || 1)}
              className="w-16 rounded-lg border border-sand-300 px-2 py-1 text-sm"
              data-testid="loops-input"
            />
            <span className="text-xs text-muted-foreground">× ({tr("common.ayahCount", { count: rangeSize })})</span>
          </label>
          <label className="flex items-center gap-2">{tr("myQuran.speed")}
            <select
              value={rate}
              onChange={(e) => {
                const r = parseFloat(e.target.value);
                setRate(r);
                if (audioRef.current) audioRef.current.playbackRate = r;
              }}
              className="rounded-lg border border-sand-300 px-2 py-1 text-sm"
              data-testid="rate-select"
            >
              {[0.5, 0.75, 1, 1.25, 1.5].map((r) => <option key={r} value={r}>{r}x</option>)}
            </select>
          </label>
          {playing && (
            <span className="text-xs text-muted-foreground" data-testid="qari-loop-progress">
              {tr("myQuran.loopProgress", { loop: loops - loopLeft + 1, loops, ayah: currentAyah - ayahStart + 1, rangeSize })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- Search */
const _DIA = /[ً-ٰٟـ﻿]/g;
const _ALIF = /[أإآٱ]/g;

function normalize(s) {
  if (!s) return "";
  return s.replace(_DIA, "").replace(_ALIF, "ا").replace(/ى/g, "ي").replace(/ة/g, "ه").toLowerCase();
}

function Highlight({ text, query }) {
  const segments = useMemo(() => {
    if (!text || !query || query.trim().length < 2) return [{ t: text, hit: false }];
    const q = normalize(query.trim());
    const out = [];
    let i = 0;
    while (i < text.length) {
      let matched = 0;
      for (let len = Math.min(text.length - i, q.length * 3); len > 0; len--) {
        if (normalize(text.slice(i, i + len)) === q) {
          matched = len;
          break;
        }
      }
      if (matched) {
        out.push({ t: text.slice(i, i + matched), hit: true });
        i += matched;
      } else {
        const start = i;
        while (i < text.length) {
          let found = false;
          for (let len = Math.min(text.length - i, q.length * 3); len > 0; len--) {
            if (normalize(text.slice(i, i + len)) === q) { found = true; break; }
          }
          if (found) break;
          i++;
        }
        out.push({ t: text.slice(start, i), hit: false });
      }
    }
    return out.length ? out : [{ t: text, hit: false }];
  }, [text, query]);

  return segments.map((s, i) =>
    s.hit
      ? <mark key={`hl-hit-${i}-${s.t}`} className="bg-gold-500/40 rounded px-0.5">{s.t}</mark>
      : <React.Fragment key={`hl-${i}-${s.t}`}>{s.t}</React.Fragment>
  );
}

function ResultCard({ r, query }) {
  const { tx, t: tr } = useLanguage();
  const isArabicQuery = /[؀-ۿ]/.test(query);
  return (
    <article className="hifz-card hover:border-forest-600/40 transition" data-testid={`search-result-${r.surah_number}-${r.verse_number}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <span className="px-2 py-0.5 rounded-full bg-forest-600/10 text-forest-700 font-semibold">
            {tr("myQuran.surahAyah", { surah_number: r.surah_number, verse_number: r.verse_number })}
          </span>
        </div>
        <Link
          to={`/app/hifz?surah=${r.surah_number}&ayah=${r.verse_number}`}
          className="inline-flex items-center gap-1 text-xs text-forest-700 hover:underline"
          data-testid={`open-${r.surah_number}-${r.verse_number}`}
        >
          {tr("myQuran.openInHifzPlan")} <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="ayah-arabic text-2xl text-foreground/95 leading-[2.2] mt-3" dir="rtl">
        {isArabicQuery ? <Highlight text={tx(r.text)} query={query} /> : r.text}
      </div>
      {r.text_en && (
        <div className="text-sm text-foreground/75 mt-2 leading-relaxed">
          {!isArabicQuery ? <Highlight text={r.text_en} query={query} /> : r.text_en}
        </div>
      )}
    </article>
  );
}

function SearchTab() {
  const { t: tr } = useLanguage();
  const [q, setQ] = useState("");
  const [surah, setSurah] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);
  const [surahList, setSurahList] = useState([]);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/quran/surahs").then((r) => setSurahList(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setData(null); setError(null); setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      const params = new URLSearchParams({ q: trimmed });
      if (surah) params.set("surah", surah);
      api.get(`/quran/search?${params.toString()}`)
        .then((r) => { setData(r.data); setError(null); })
        .catch((e) => { setError(e?.response?.data?.detail || tr("myQuran.searchFailed")); setData(null); })
        .finally(() => setLoading(false));
    }, 280);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, surah]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const clear = () => { setQ(""); setSurah(""); setData(null); inputRef.current?.focus(); };

  return (
    <div className="space-y-6" data-testid="quran-search-tab">
      <div className="hifz-card space-y-3" data-testid="search-controls">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setTouched(true); }}
            placeholder="e.g.  الرحمن  or  patience  or  لا اله الا الله"
            className="w-full rounded-2xl border border-sand-300 bg-white pl-12 pr-12 py-4 text-base focus:outline-none focus:ring-2 focus:ring-forest-600/40 focus:border-forest-600"
            data-testid="search-input"
          />
          {q && (
            <button
              type="button"
              onClick={clear}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-sand-200 text-muted-foreground"
              data-testid="search-clear"
              aria-label={tr("myQuran.clearSearch")}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{tr("myQuran.surahFilter")}</label>
          <select
            value={surah}
            onChange={(e) => setSurah(e.target.value)}
            className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm"
            data-testid="search-surah-filter"
          >
            <option value="">{tr("myQuran.all114Surahs")}</option>
            {surahList.map((s) => (
              <option key={s.number} value={s.number}>
                {`${s.number}. ${s.name_en || `Surah ${s.number}`}`}
              </option>
            ))}
          </select>
          {surah && (
            <button onClick={() => setSurah("")} className="text-xs text-terracotta-600 hover:underline" data-testid="search-surah-clear">
              {tr("myQuran.clearFilter")}
            </button>
          )}
        </div>
      </div>

      <div className="text-sm text-muted-foreground" aria-live="polite" data-testid="search-status">
        {!touched && !q && tr("myQuran.type2CharactersToBegin")}
        {q && q.trim().length < 2 && tr("myQuran.keepTypingAtLeast2")}
        {loading && (
          <span className="inline-flex items-center gap-1.5 text-forest-700">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> {tr("myQuran.searching")}
          </span>
        )}
        {!loading && data && (
          <>
            <span className="text-forest-700 font-semibold" data-testid="search-result-count">{data.total_results}</span>
            {" "}{tr("myQuran.matchesFor", { count: data.total_results })} &ldquo;<span className="font-medium">{data.query}</span>&rdquo;
            {surah && <> {tr("myQuran.inSurah", { surah })}</>}
            {data.total_results === 50 && <span className="ml-1 opacity-70">{tr("myQuran.top50ByRelevance")}</span>}
          </>
        )}
        {error && <span className="text-terracotta-600">{error}</span>}
      </div>

      {/* C3 — a query can match a surah by name/translation (e.g. "mulk")
          with zero literal ayah-text matches; show that separately so
          "1 match" in the status line above isn't followed by an empty
          results list. Any surah is readable via SurahView, so this can
          link straight there (unlike the embedded mushaf reader's inline
          search, which is capped to the two Madani-page-glyph surahs). */}
      {data && data.surah_matches?.length > 0 && (
        <div className="space-y-2" data-testid="search-surah-matches">
          {data.surah_matches.map((s) => (
            <Link
              key={s.number}
              to={`/app/hifz/${s.number}`}
              className="hifz-card hifz-card-hover flex items-center justify-between gap-3"
              data-testid={`search-surah-match-${s.number}`}
            >
              <div>
                <div className="font-display font-semibold text-forest-700">{tr("myQuran.surah", { number: s.number, name_en: s.name_en })}</div>
                <div className="text-xs text-muted-foreground">{tr("myQuran.ayahs", { name_translation: s.name_translation, total_ayahs: s.total_ayahs })}</div>
              </div>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </Link>
          ))}
        </div>
      )}

      {data && data.results.length > 0 && (
        <div className="space-y-3" data-testid="search-results">
          {data.results.map((r) => <ResultCard key={r.id} r={r} query={q} />)}
        </div>
      )}

      {data && data.results.length === 0 && !data.surah_matches?.length && !loading && (
        <div className="hifz-card text-center py-16 text-muted-foreground" data-testid="search-empty">
          <BookOpen className="h-10 w-10 mx-auto opacity-40" />
          <p className="mt-3">{surah ? tr("myQuran.noMatchesInSurah", { query: data.query, surah }) : tr("myQuran.noMatchesFor", { query: data.query })}</p>
          <p className="text-xs mt-1 opacity-70">{tr("myQuran.tryAShorterQueryRemove")}</p>
        </div>
      )}
    </div>
  );
}
